import React, { useState, useEffect, useCallback, useMemo } from 'react';
import adler32 from 'adler-32';
import { Col, Div, Icon, Input, Label, Row, Switch, Text } from '@pinpoint/design-kit';
import isEmpty from 'lodash.isempty';

import CONSTANTS from 'src/store/constants';
import { Card, FileDrop, StepperProgress, Button } from 'src/components';

/* eslint-disable react/prop-types */

const TOTAL_PAGES = 11;

export const cardProps = {
  iconProps: {
    name: 'ActuarialModel',
    color: 'white',
    size: '3.125rem',
    viewBox: '0 0 12 12',
  },
  titleProps: {
    bg: 'teal',
    h: '5.5rem',
    p: { l: '2.5rem', r: '2.625rem' },
    rounded: { tr: 'md', tl: 'md' },
  },
  titleTextProps: {
    textColor: 'white',
    textSize: 'title',
    lineHeight: 'title',
  },
  cardProps: {
    p: '0',
    textSize: 'paragraph',
    style: {
      lineHeight: '1.5rem',
    },
    rounded: 'md',
    textColor: 'shade66',
  },
};

export function TopButtons() {
  return (
    <Row p={{ b: '1.25rem' }}>
      <Col size="12">
        <Div m={{ t: '1rem' }} d="flex" justify="flex-start">
          <Button
            buttonStyle="primary"
            onClick={() => {
              window.open('/static/Pinpoint_Predictive_Actuarial_Loss_Predictions_Requirements_16-VI-25.pdf');
            }}
            data-tip="Download Actuarial requirements"
            style={{
              flex: '0 0 auto',
            }}
          >
            <Icon name="Download2" viewBox="0 0 14 14" color="white" m={{ r: '0.5rem' }} size="0.875rem" />
            ACTUARIAL REQUIREMENTS
          </Button>
          <Button
            buttonStyle="primary"
            onClick={() => {
              window.open('/static/Pinpoint_Actuarial_Onboarding_Template_Updated_16-VI-25.xlsx');
            }}
            data-tip="Download Actuarial template"
            style={{
              flex: '0 0 auto',
            }}
          >
            <Icon name="Download2" viewBox="0 0 14 14" color="white" m={{ r: '0.5rem' }} size="0.875rem" />
            ACTUARIAL TEMPLATE
          </Button>
        </Div>
      </Col>
    </Row>
  );
}

function BottomButtons({ onBack, onNext, isNextDisabled, isNextShown, isSkipReqShown, onSkipReq }) {
  return (
    <Row>
      <Col size="12">
        <Div m={{ t: '1rem', x: '-0.5rem' }} d="flex" justify="space-between">
          <Button
            buttonStyle="primary"
            m={{ l: '0.5rem' }}
            onClick={() => {
              onBack();
            }}
            style={{
              flex: '0 0 auto',
            }}
          >
            <Icon color="white" name="LongLeft" size="1rem" m={{ r: '0.5rem' }} />
            BACK
          </Button>
          <Div d="flex">
            {isSkipReqShown ? (
              <Button
                buttonStyle="primary"
                onClick={onSkipReq}
                style={{
                  flex: '0 0 auto',
                }}
              >
                SKIP REQUIREMENTS
                <Icon color="white" name="DoubleArrow" size="0.75rem" m={{ l: '0.5rem' }} viewBox="0 0 12 12" />
              </Button>
            ) : null}
            {isNextShown ? (
              <Button
                buttonStyle="primary"
                onClick={() => {
                  onNext();
                }}
                style={{
                  flex: '0 0 auto',
                }}
                disabled={isNextDisabled}
              >
                NEXT
                <Icon color="white" name="LongRight" size="1rem" m={{ l: '0.5rem' }} />
              </Button>
            ) : null}
          </Div>
        </Div>
      </Col>
    </Row>
  );
}

function BusinessAndCoverages({ setTitle, disableNext, showNext }) {
  useEffect(() => {
    setTitle('Line of business and coverages considerations');
    disableNext(false);
    showNext(true);
  }, [setTitle, disableNext, showNext]);

  const styles = useMemo(
    () => ({
      customColumnName: {
        fontWeight: 'bold',
      },
    }),
    []
  );

  return (
    <Text style={{ listStyle: 'outside' }}>
      <strong style={styles.customColumnName}>Homeowners</strong>
      <Text tag="ul" m={{ l: '1rem' }} style={{ listStyle: 'outside' }}>
        <li>
          Only HO3 or HO5 coverages; do not mix tenants/renters, condo, or dwelling policies into a HO3 or HO5 model.
        </li>
        <li>
          Large carriers may wish to model each major non-cat peril separately, in which case a separate file should be
          prepared for each peril. Smaller and midsize home insurers should create a single roll-up model that includes
          non-cat water, wind, and hail perils only. Non-cat fire should be modeled separately, provided sufficient data
          volumes are available.
        </li>
      </Text>
      <br />
      <strong style={styles.customColumnName}>Auto</strong>
      <Text tag="ul" m={{ l: '1rem' }} style={{ listStyle: 'outside' }}>
        <li>
          Carriers with sufficient PIF may wish to model each major coverage (e.g., Physical Damage, Collision, Bodily
          Injury, etc.) separately, in which case a separate file should be prepared for each coverage. Carriers with
          smaller PIF counts should roll-up all coverages into the same dataset.
        </li>
      </Text>
      <br />
      <strong style={styles.customColumnName}>Other</strong>
      <Text tag="ul" m={{ l: '1rem' }} style={{ listStyle: 'outside' }}>
        <li>
          Carriers may want to create models for other lines of business including but not limited to HO4, HO6, HO2, or
          small commercial lines. These alternative lines of business should be discussed with Pinpoint to ensure
          feasibility and proper data structure.
        </li>
      </Text>
    </Text>
  );
}

function NumberFilesModels({ setTitle, disableNext, showNext }) {
  useEffect(() => {
    setTitle('Number of files and models');
    disableNext(false);
    showNext(true);
  }, [setTitle, disableNext, showNext]);

  const styles = useMemo(
    () => ({
      customColumnName: {
        fontWeight: 'bold',
      },
    }),
    []
  );

  return (
    <Text style={{ listStyle: 'outside' }}>
      Each Actuarial Loss Dataset requires the preparation and upload of only a single file, which the platform will use
      to train and validate four different model types:
      <Text tag="ul" m={{ l: '1rem' }} style={{ listStyle: 'outside' }}>
        <br />
        <li>
          <strong style={styles.customColumnName}>Claims Frequency</strong>: Predicts the number of claims per exposure
          unit. If TG_OtherExposure is not used, then Claims Frequency is claims per policy period. If present, then
          Claims Frequency is claims per TG_OtherExposure unit.
        </li>
        <br />
        <li>
          <strong style={styles.customColumnName}>Claims Severity</strong>: Predicts the dollar cost of an average loss
          for this policy.
        </li>
        <br />
        <li>
          <strong style={styles.customColumnName}>Loss Cost Multiplied</strong>: Also known as Pure Premium, this is the
          multiplied product of Pinpoint’s Claims Frequency and Severity predictions.
        </li>
        <br />
        <li>
          <strong style={styles.customColumnName}>Loss Cost Direct</strong>: A model that targets Loss Cost directly,
          estimating the cost to insure the policy per unit of exposure.
        </li>
      </Text>
    </Text>
  );
}

function LookbackWindow({ setTitle, disableNext, showNext }) {
  useEffect(() => {
    setTitle('Lookback window');
    disableNext(false);
    showNext(true);
  }, [setTitle, disableNext, showNext]);

  const styles = useMemo(
    () => ({
      customColumnName: {
        fontStyle: 'italic',
      },
    }),
    []
  );

  return (
    <Text tag="ul" m={{ l: '1rem' }} style={{ listStyle: 'outside' }}>
      <li>
        Select all primary insureds effective at any time over the past 4 years, as well as their relevant loss data
        corresponding to only this same lookback period.
      </li>
      <br />
      <li>
        While no outcomes should go back further than 4 years, you may wish for the lookback window to end earlier than
        the present, to allow sufficient time for loss development.
      </li>
      <br />
      <li>
        Each Record (row) must contain a unique primary insured whose outcome data is rolled up into that single row.{' '}
        <strong style={styles.customColumnName}>
          Example: If a principal insured has been a policyholder for 10 years, you should only include in their Record
          their loss outcomes available at any point over the past 4 years.
        </strong>
      </li>
      <br />
      <li>
        If there are multiple insureds on a policy, roll up the policy’s loss outcomes to one Record for the primary
        insured or the first listed.
      </li>
    </Text>
  );
}

function MinAndMaxRecordVolumes({ setTitle, disableNext, showNext }) {
  useEffect(() => {
    setTitle('Minimum and maximum record volumes');
    disableNext(false);
    showNext(true);
  }, [setTitle, disableNext, showNext]);

  return (
    <Text>
      To attain the best performance, please include all Records you have over the Lookback Period. You must have at
      least 50k primary insureds active over this period to build Actuarial Loss Models. The maximum number of Records
      you may include in an Actuarial Loss Training Dataset is currently 5M Records.
    </Text>
  );
}

function RecordTypes({ setTitle, disableNext, showNext }) {
  useEffect(() => {
    setTitle('Record types');
    disableNext(false);
    showNext(true);
  }, [setTitle, disableNext, showNext]);

  return (
    <Text>
      All records for a given Actuarial Loss model must be aggregated into a single .csv file. The OnPoint platform
      automatically reads the field values within the file to determine which records are (i) Training Records with
      Claims, (ii) Training Records without Claims, or (iii) Blind Validation Records. The following sections specify
      how each Record type should be prepared.
    </Text>
  );
}

function TrainingRecordsClaims({ setTitle, disableNext, showNext }) {
  useEffect(() => {
    setTitle('Training records with claims');
    disableNext(false);
    showNext(true);
  }, [setTitle, disableNext, showNext]);

  const styles = useMemo(
    () => ({
      customColumnName: {
        fontWeight: 'bold',
        fontStyle: 'italic',
      },
      italic: {
        fontStyle: 'italic',
      },
      bold: {
        fontWeight: 'bold',
      },
    }),
    []
  );

  return (
    <Text tag="ul" m={{ l: '1rem' }} style={{ listStyle: 'outside' }}>
      <li>
        These are defined as Records that have a <strong style={styles.customColumnName}>TG_SumLossCounts</strong> value
        of 1 or greater.
      </li>
      <li>
        The following fields are always required for Records with claims:
        <ul style={{ listStyle: 'circle', paddingLeft: 'revert' }}>
          <li>
            <strong style={styles.customColumnName}>EffectiveDate</strong> –{' '}
            <span style={styles.italic}>
              must be the earliest effective date within the lookback window, formatted as YYYY-MM-DD.
            </span>
          </li>
          <li>
            <strong style={styles.customColumnName}>TG_SumLossCounts</strong>
          </li>
          <li>
            <strong style={styles.customColumnName}>TG_SumPremiumsEarned</strong> –{' '}
            <span style={styles.italic}>
              Pinpoint strongly <strong style={styles.bold}>recommends</strong> that the premiums be on-leveled to
              current rates. This will allow a more accurate estimate of the impact on future Loss Ratio during model
              validation.
            </span>
          </li>
          <li>
            <strong style={styles.customColumnName}>TG_SumLossDollarsAndExpenses</strong> –{' '}
            <span style={styles.italic}>
              Loss and expense dollars can either be the raw incurred plus expense or can be trended and/or developed
              depending on data restraints and use cases.
            </span>
          </li>
          <li>
            <strong style={styles.customColumnName}>TG_SumPolicyPeriods</strong> –{' '}
            <span style={styles.italic}>
              These values must be non-zero positive numbers, where 1.00 units equal six months for all Personal Auto
              policies, and twelve months for all other Lines of Business.
            </span>
          </li>
        </ul>
      </li>
      <li>
        The column <strong style={styles.customColumnName}>TG_OtherExposure</strong> allows carriers to indicate
        exposure; if omitted, then <strong style={styles.customColumnName}>TG_SumPolicyPeriods</strong> will be used as
        exposure.
        <ul style={{ listStyle: 'circle', paddingLeft: 'revert' }}>
          <li>
            For Personal Auto, carriers can optionally use this field for the sum of earned vehicle years, months, days,
            or mileage.
          </li>
          <li>
            For most Commercial Lines applications, carriers should choose an appropriate exposure for this field, so
            that exposure accounts for business size and class (Pinpoint’s Predictions account for only the risk of the
            individual owner/operator – not for business features like headcount or class code).
          </li>
          <li>Values must be non-zero positive numbers with two or more decimal places.</li>
        </ul>
      </li>
      <li>Do not include any dollar signs or negative numbers anywhere.</li>
    </Text>
  );
}

function TrainingRecordsNoClaims({ setTitle, disableNext, showNext }) {
  useEffect(() => {
    setTitle('Training records without claims');
    disableNext(false);
    showNext(true);
  }, [setTitle, disableNext, showNext]);

  const styles = useMemo(
    () => ({
      customColumnName: {
        fontWeight: 'bold',
        fontStyle: 'italic',
      },
    }),
    []
  );

  return (
    <Text tag="ul" m={{ l: '1rem' }} style={{ listStyle: 'outside' }}>
      <li>
        The non-claims Training Records must have a zero (0) entered in the
        <strong style={styles.customColumnName}>TG_SumLossCounts</strong> column.
      </li>
      <br />
      <li>
        Values are also required for <strong style={styles.customColumnName}>EffectiveDate</strong>,{' '}
        <strong style={styles.customColumnName}>TG_SumLossDollarsAndExpenses</strong> (entering zeros where needed),{' '}
        <strong style={styles.customColumnName}>TG_SumPremiumsEarned</strong>, and
        <strong style={styles.customColumnName}>TG_SumPolicyPeriods</strong> in all cases, and
        <strong style={styles.customColumnName}>TG_OtherExposure</strong> when this column is present.
      </li>
      <br />
      <li>Do not include any dollar signs, negative numbers, or “Null” or “N/A” values anywhere.</li>
    </Text>
  );
}

function BlindValidation({ setTitle, disableNext, showNext }) {
  useEffect(() => {
    setTitle('Blind validation records');
    disableNext(false);
    showNext(true);
  }, [setTitle, disableNext, showNext]);

  return (
    <Text tag="ul" m={{ l: '1rem' }} style={{ listStyle: 'outside' }}>
      <li>
        <strong style={{ fontWeight: 'bold' }}>Data Volumes:</strong> Please also ensure that 20% of the Records in your
        Actuarial Loss dataset are blind Validation Records. These Records must be mutually exclusive from, but fully
        representative of, the claims and non-claims Training Records described earlier.
      </li>
      <br />
      <li>
        <strong style={{ fontWeight: 'bold' }}>Loss Columns:</strong> Validation Records&apos; values in the loss
        columns must be left as empty strings (i.e. no values entered in these cells) and not zeros, to be recognized by
        our platform as Blind Validation Records.
      </li>
      <br />
      <li>
        We recommend entering actual values into{' '}
        <strong style={{ fontWeight: 'bold', fontStyle: 'italic' }}>TG_SumPremiumsEarned</strong> for these Validation
        Records.
      </li>
    </Text>
  );
}

function ProportionRecords({ setTitle, disableNext, showNext }) {
  useEffect(() => {
    setTitle('Proportion of records');
    disableNext(false);
    showNext(true);
  }, [setTitle, disableNext, showNext]);

  return (
    <Text>
      The breakdown of Records with claims and Records without Claims, in both the Training Set and Blind Validation
      Set, should be proportional to their actual frequency during the lookback period.
    </Text>
  );
}

function OnboardingChecklist({ setTitle, disableNext, showNext }) {
  const leftItems = [
    'The only headers in my .csv are exactly the same as in the onboarding template.',
    "If certain PII are not available for specific fields, they've been left as empty strings.",
    'Each person record is present in only one row and has a unique value under the "CRM ID" header; multiple policy periods corresponding to the same person have been collapsed into a single row.',
    "Where relevant, Address Line 1 and Address Line 2 fields are concatenated with a space between them, and placed in the 'Address' column.",
    'All state names are capitalized two-letter abbreviations.',
  ];
  const rightItems = [
    'ZIP is always 5 digits long (with an initial zero if necessary); if present, ZIP+4 is always 4 digits long and in a separate column.',
    'Phone values have exactly 10 digits with no special characters.',
    'Dollar signs are not included anywhere.',
    'Values in column R (and in column S, where relevant) are all non-zero positive numbers.',
    'The file is a UTF-8 encoded .csv. It has a transparent name and a .csv extension.',
  ];

  const [responses, setResponses] = useState(new Array(10).fill(false));

  useEffect(() => {
    setTitle('Pre-onboarding checklist');
    disableNext(true);
    showNext(true);
  }, [setTitle, disableNext, showNext]);

  useEffect(() => {
    disableNext(!responses.every((x) => x));
  }, [responses, disableNext]);

  return (
    <Div>
      <Row>
        <Col size="6">
          {leftItems.map((item, index) => (
            <Div d="flex" m={{ y: '1rem' }} key={`left.${adler32.str(leftItems[index])}`}>
              <Label
                tag="div"
                align="center"
                data-test={`left.${index}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setResponses((resp) => {
                    const nextResp = [...resp];
                    nextResp[index] = !resp[index];
                    return nextResp;
                  });
                }}
              >
                <Switch checked={responses[index]} activeColor="brand500" activeShadow="0" />
              </Label>
              <Text>{item}</Text>
            </Div>
          ))}
        </Col>
        <Col size="6">
          {rightItems.map((item, index) => (
            <Div d="flex" m={{ y: '1rem' }} key={`right.${adler32.str(rightItems[index])}`}>
              <Label
                tag="div"
                align="center"
                data-test={`right.${index}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setResponses((resp) => {
                    const nextResp = [...resp];
                    nextResp[index + leftItems.length] = !resp[index + leftItems.length];
                    return nextResp;
                  });
                }}
              >
                <Switch checked={responses[index + leftItems.length]} activeColor="brand500" activeShadow="0" />
              </Label>
              <Text>{item}</Text>
            </Div>
          ))}
        </Col>
      </Row>
    </Div>
  );
}

function FileUpload({
  setTitle,
  datasetTag,
  handleFileDrop,
  handleFileSelect,
  files,
  stageFiles,
  progress,
  uploadFileStatus,
  datasetName,
  isAdmin,
  isSuperAdmin,
  compState,
  triggerDetWorkflowStatus,
  // functions
  getTagStyles,
  setDatasetName,
  uploadFiles,
  setCompState,
  showNext,
  defaultRefreshId,
  refreshId,
  refreshes,
  setRefreshId,
  lockedFile,
}) {
  const [defaultRefresh, setDefaultRefresh] = useState({});
  const [refresh, setRefresh] = useState(/** @type {import('src/store/typings').Refresh} */ ({}));

  useEffect(() => {
    setTitle('Select file to upload');
    showNext(false);
  }, [setTitle, showNext]);

  useEffect(() => {
    if (isEmpty(defaultRefresh)) setDefaultRefresh(refreshes?.find((r) => r.id === defaultRefreshId));
    else if (!refreshId) setRefreshId(defaultRefreshId);
    else if (refreshId && refresh?.id !== refreshId) setRefresh(refreshes?.find((r) => r.id === refreshId));
  }, [defaultRefresh, defaultRefreshId, refresh?.id, refreshId, refreshes, setRefreshId]);

  return (
    <Div w="100%">
      <Row>
        <Col size="5">
          <FileDrop
            onDrop={handleFileDrop}
            onSelect={handleFileSelect}
            droppedFiles={files}
            setDroppedFiles={stageFiles}
            progress={progress}
            uploadFileStatus={uploadFileStatus}
            locked={lockedFile}
          />
        </Col>
        <Col size="7">
          Dataset Name
          <Input
            value={datasetName}
            onChange={(e) => setDatasetName(e.target.value)}
            h="1.875rem"
            m={{ b: '.875rem', t: '.25rem' }}
            data-test="dataset-name"
          />
          {datasetName.trim().length > 120 && (
            <Text textColor="danger800" textSize="caption" m={{ t: '.125rem' }}>
              Dataset name cannot be longer than 120 characters
            </Text>
          )}
          Type
          <Div
            d="flex"
            align="center"
            border="1px solid"
            borderColor="gray500"
            rounded="md"
            p={{ x: '.75rem' }}
            m={{ b: '.875rem', t: '.25rem' }}
            h="1.875rem"
          >
            <Icon size=".875rem" m={{ r: '.5em' }} alignSelf="center" {...getTagStyles(datasetTag)} />
            <Text textSize="body" textColor={getTagStyles(datasetTag).color}>
              {CONSTANTS.SEGMENT.TAG_NAME[datasetTag]}
            </Text>
          </Div>
          {isSuperAdmin ? (
            <Div>
              Refresh
              <Input
                data-test="refresh-name"
                isLoading={!refreshId}
                value={refresh?.name}
                disabled
                h="2.4rem"
                m={{ b: '14px' }}
              />
            </Div>
          ) : null}
          <Div m={{ t: '2.5rem' }} d="flex" justify="flex-end">
            <Button
              buttonStyle="primary"
              m={{ r: '0rem' }}
              isLoading={compState !== CONSTANTS.COMPONENT.VALIDATION.VALIDATED}
              disabled={
                !isAdmin ||
                uploadFileStatus === CONSTANTS.API.STARTED ||
                triggerDetWorkflowStatus === CONSTANTS.API.STARTED ||
                files.length === 0 ||
                !datasetName.trim() ||
                compState !== CONSTANTS.COMPONENT.VALIDATION.VALIDATED ||
                datasetName.trim().length > 120
              }
              loading={uploadFileStatus === CONSTANTS.API.STARTED}
              onClick={() => {
                setCompState(CONSTANTS.COMPONENT.OPERATION.STARTED);
                uploadFiles(files, CONSTANTS.S3_PREFIX.ONPOINT_WORKFLOW);
                setDatasetName(datasetName.trim());
              }}
            >
              UPLOAD
              <Icon name="UploadForward" viewBox="0 0 14 16" color="white" m={{ l: '0.5rem' }} size="0.875rem" />
            </Button>
          </Div>
        </Col>
      </Row>
    </Div>
  );
}

const renderComponentByPage = ({ ...rest }) => {
  const list = [
    <BusinessAndCoverages {...rest} />,
    <NumberFilesModels {...rest} />,
    <LookbackWindow {...rest} />,
    <MinAndMaxRecordVolumes {...rest} />,
    <RecordTypes {...rest} />,
    <TrainingRecordsClaims {...rest} />,
    <TrainingRecordsNoClaims {...rest} />,
    <BlindValidation {...rest} />,
    <ProportionRecords {...rest} />,
    <OnboardingChecklist {...rest} />,
    <FileUpload {...rest} />,
  ];
  if (rest.page && rest.page > list.length && rest.page > 0) {
    return null;
  }
  return list[rest.page];
};

function Actuarial({ onDone, goBack, ...props }) {
  const [page, setPage] = useState(0);
  const [isNextDisabled, disableNext] = useState(false);
  const [isNextShown, showNext] = useState(true);
  const [isSkipReqShown, setIsSkipReqShown] = useState(+localStorage.getItem('reviewedActuarialInstr'));
  const [title, setTitle] = useState('Actuarial model overview');
  const { setDatasetTag, triggerDetWorkflowStatus } = props;

  const handleSkipReq = useCallback(() => setPage(TOTAL_PAGES - 1), []);

  const onSkip = useCallback(() => {
    if (page < TOTAL_PAGES - 1) return setPage(page + 1);
    return onDone();
  }, [page, onDone]);

  useEffect(() => {
    const nextTag = CONSTANTS.SEGMENT.TAG.ACTUARIAL;
    setDatasetTag(nextTag);
    if (TOTAL_PAGES - 1 === page) localStorage.setItem('reviewedActuarialInstr', '1');
    if (page === TOTAL_PAGES - 1) setIsSkipReqShown(0);
    else setIsSkipReqShown(+localStorage.getItem('reviewedActuarialInstr'));
  }, [page, setIsSkipReqShown, setDatasetTag]);

  useEffect(() => {
    if (triggerDetWorkflowStatus === CONSTANTS.API.SUCCEEDED) onSkip();
  }, [triggerDetWorkflowStatus, onSkip]);

  return (
    <Div>
      <TopButtons />
      <Card
        title={title}
        iconProps={cardProps.iconProps}
        titleProps={cardProps.titleProps}
        titleTextProps={cardProps.titleTextProps}
        cardProps={cardProps.cardProps}
      >
        <Row m="0" minH="18rem" p={{ l: '2.5rem', r: '2.5rem', t: '1.75rem', b: '1.875rem' }}>
          {renderComponentByPage({ page, setTitle, disableNext, showNext, onSkip, ...props })}
        </Row>
      </Card>
      <StepperProgress currentPage={page} totalPages={TOTAL_PAGES} />
      <BottomButtons
        onBack={() => {
          if (page > 0) {
            setPage((currPage) => currPage - 1);
          } else {
            goBack();
          }
        }}
        onNext={onSkip}
        isNextDisabled={isNextDisabled}
        isNextShown={isNextShown}
        isSkipReqShown={isSkipReqShown}
        onSkipReq={handleSkipReq}
      />
    </Div>
  );
}

export default Actuarial;
